import React, { useEffect, useState, useRef, useCallback } from 'react';
import { Table, Alert, Spin, Button, Modal, Select, Input, message, Tag, Row, Col, Empty } from 'antd';
import { SearchOutlined, LoadingOutlined } from '@ant-design/icons';
import { useDispatch, useSelector } from 'react-redux';
import { getAllAdminUsers, updateUserStatus, deleteAdminUser } from '../../store/features/dashboard/adminUsersSlice';
import { debounce } from '../../lib/utils';

const { Option } = Select;

const AdminUsers = () => {
  const dispatch = useDispatch();
  const { 
    adminUsers, 
    total, 
    loading, 
    error,
    currentPage,
    pageSize,
  } = useSelector((state) => state.adminUsers);
  
  const [searchText, setSearchText] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [isSearching, setIsSearching] = useState(false);
  const searchInputRef = useRef(null);

  const fetchAdminUsers = useCallback((page, size, search, status) => {
    setIsSearching(true);
    dispatch(getAllAdminUsers({ 
      page, 
      limit: size,
      search,
      status: status !== 'all' ? status : undefined
    })).finally(() => {
      setIsSearching(false);
      if (searchInputRef.current) {
        searchInputRef.current.focus();
      }
    });
  }, [dispatch]);

  const debouncedSearch = useCallback(
    debounce((searchValue) => {
      if (searchValue.length >= 2 || searchValue.length === 0) {
        fetchAdminUsers(currentPage, pageSize, searchValue, statusFilter);
      }
    }, 500),
    [fetchAdminUsers, currentPage, pageSize, statusFilter]
  );

  useEffect(() => {
    fetchAdminUsers(currentPage, pageSize, searchText, statusFilter);
  }, [fetchAdminUsers, currentPage, pageSize, statusFilter]);

  const handleSearch = (value, event) => {
    setSearchText(value);
    
    if (event && event.type === 'change') {
      debouncedSearch(value);
    } else {
      fetchAdminUsers(currentPage, pageSize, value, statusFilter);
    }
  };

  const handleClearSearch = () => {
    setSearchText('');
    setStatusFilter('all');
    fetchAdminUsers(currentPage, pageSize, '', 'all');
    if (searchInputRef.current) {
      searchInputRef.current.focus();
    }
  };

  const handleStatusFilterChange = (value) => {
    setStatusFilter(value);
    fetchAdminUsers(currentPage, pageSize, searchText, value);
  };

  const handlePageChange = (page, size) => {
    fetchAdminUsers(page, size, searchText, statusFilter);
  };

  const handleUpdateStatus = (userId, status) => {
    Modal.confirm({
      title: `Are you sure you want to update this user to ${status}?`,
      okText: 'Yes, update',
      okType: 'primary',
      cancelText: 'No',
      onOk() {
        dispatch(updateUserStatus({ id: userId, status }))
          .unwrap()
          .then(() => {
            message.success('User status updated successfully');
            fetchAdminUsers(currentPage, pageSize, searchText, statusFilter);
          })
          .catch((err) => {
            message.error(err || 'Failed to update user status');
          });
      },
    });
  };

  const handleDelete = (userId) => {
    Modal.confirm({
      title: 'Are you sure you want to soft delete this user?',
      content: 'This action will mark the user as deleted but can be restored',
      okText: 'Yes, delete',
      okType: 'danger',
      cancelText: 'No',
      onOk() {
        dispatch(deleteAdminUser(userId))
          .unwrap()
          .then(() => {
            message.success('User soft deleted successfully');
            fetchAdminUsers(currentPage, pageSize, searchText, statusFilter);
          })
          .catch((err) => {
            message.error(err || 'Failed to delete user');
          });
      },
    });
  };

  const columns = [
    {
      title: 'Status',
      dataIndex: 'status',
      key: 'status',
      width: 120,
      fixed: 'left',
      render: (status) => {
        const colorMap = {
          new: 'blue',
          authorized: 'green',
          unauthorized: 'orange',
          rejected: 'red',
          deleted: 'grey'
        };
        return <Tag color={colorMap[status]}>{status.toUpperCase()}</Tag>;
      },
    },
    {
      title: 'Name',
      dataIndex: 'name',
      key: 'name',
      width: 150,
    },
    {
      title: 'Email',
      dataIndex: 'email',
      key: 'email',
      width: 200,
    },
    {
      title: 'Created At',
      dataIndex: 'createdAt',
      key: 'createdAt',
      width: 150,
      render: (date) => new Date(date).toLocaleDateString(),
    },
    {
      title: 'Actions',
      key: 'actions',
      width: 200,
      fixed: 'right',
      render: (_, record) => (
        <div style={{ display: 'flex', gap: '8px' }}>
          <Select
            size="small"
            defaultValue={record.status}
            style={{ width: 120 }}
            onChange={(value) => handleUpdateStatus(record.id, value)}
            disabled={record.status === 'deleted'}
          >
            <Option value="new">New</Option>
            <Option value="authorized">Authorized</Option>
            <Option value="unauthorized">Unauthorized</Option>
            <Option value="rejected">Rejected</Option>
          </Select>
          <Button 
            type="primary" 
            danger 
            size="small"
            onClick={() => handleDelete(record.id)}
            disabled={record.status === 'deleted'}
          >
            Delete
          </Button>
        </div>
      ),
    },
  ];

  if (loading && adminUsers.length === 0) {
    return (
      <div style={{ display: 'flex', justifyContent: 'center', padding: '24px' }}>
        <Spin size="large" />
      </div>
    );
  }

  if (error) {
    return (
      <Alert
        message="Error Loading Admin Users"
        description={error}
        type="error"
        showIcon
        style={{ margin: '24px' }}
      />
    );
  }

  return (
    <div style={{ 
      padding: '24px',
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center',
      width: '100%'
    }}>
      <h2 style={{ marginBottom: '16px' }}>Admin Users Management</h2>
      
      <div style={{ 
        width: '100%',
        maxWidth: '1200px',
        marginBottom: '16px'
      }}>
        <Row justify="space-between" align="middle" gutter={16}>
          <Col>
            <Select
            allowClear
              value={statusFilter}
              style={{ width: 150 }}
              onChange={handleStatusFilterChange}
            >
              <Option value="all">All Statuses</Option>
              <Option value="new">New</Option>
              <Option value="authorized">Authorized</Option>
              <Option value="unauthorized">Unauthorized</Option>
              <Option value="rejected">Rejected</Option>
              <Option value="deleted">Deleted</Option>
            </Select>
          </Col>
          <Col>
            <Input.Search
              ref={searchInputRef}
              placeholder="Search by name or email..."
              allowClear
              enterButton={isSearching ? <LoadingOutlined /> : <SearchOutlined />}
              size="large"
              style={{ width: 300 }}
              value={searchText}
              onChange={(e) => handleSearch(e.target.value, e)}
              onSearch={(value) => handleSearch(value, { type: 'search' })}
              onClear={handleClearSearch}
              loading={isSearching}
            />
          </Col>
        </Row>
      </div>

      <div style={{ 
        width: '100%',
        maxWidth: '1200px',
      }}>
        {adminUsers.length === 0 && !loading ? (
          <Empty
            description={
              searchText || statusFilter !== 'all' 
                ? `No admin users found matching your criteria` 
                : 'No admin users available'
            }
            style={{ margin: '40px 0' }}
          />
        ) : (
          <Table
            columns={columns}
            dataSource={adminUsers}
            loading={loading}
            rowKey="id"
            bordered
            scroll={{
              x: 'max-content',
              y: 'calc(100vh - 340px)'
            }}
            pagination={{
              current: currentPage,
              pageSize: pageSize,
              total: total,
              onChange: handlePageChange,
              onShowSizeChange: handlePageChange,
              showSizeChanger: true,
              pageSizeOptions: ['5', '10', '15', '20'],
              showTotal: (total, range) => (
                <span>
                  Showing {range[0]}-{range[1]} of {total} records
                </span>
              ),
              position: ['bottomCenter'],
            }}
            style={{ width: '100%' }}
          />
        )}
      </div>
    </div>
  );
};

export default AdminUsers;